import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'

export const CookiePolicyPage: React.FC = () => {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-4 text-blue-600 hover:text-blue-800"
          >
            ← Back to Home
          </Button>
          
          <div className="text-center mb-6">
            <div className="text-3xl font-bold text-gray-900 mb-2">
              Strat<span className="text-blue-600">IQ</span><span className="text-cyan-600">X</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Cookie Policy</h1>
            <p className="text-gray-600">Preview Version - Effective Date: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        <Card className="shadow-lg border-0 bg-white">
          <CardContent className="p-8 prose prose-blue max-w-none">
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 font-medium mb-2">🍪 Preview Cookie Notice</p>
              <p className="text-blue-700 text-sm">
                This cookie policy applies to the preview version of StratIQX. We use minimal cookies during preview for basic functionality and analytics.
              </p>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. What Are Cookies</h2>
            <p className="text-gray-600 mb-6">
              Cookies are small text files that are placed on your computer or mobile device when you visit our website. 
              They help us provide you with a better experience by remembering your preferences and understanding how you use our platform.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. How We Use Cookies</h2>
            
            <h3 className="text-lg font-medium text-gray-800 mb-2">Essential Cookies</h3>
            <p className="text-gray-600 mb-2">
              These cookies are necessary for the platform to function properly:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li><strong>Session Management:</strong> Keep you logged in during your visit</li>
              <li><strong>Security:</strong> Protect against cross-site request forgery</li>
              <li><strong>Progress Saving:</strong> Remember your onboarding progress</li>
              <li><strong>User Preferences:</strong> Store your settings and preferences</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">Analytics Cookies</h3>
            <p className="text-gray-600 mb-2">
              These help us understand how visitors interact with our platform:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li><strong>Usage Statistics:</strong> Track page views and user interactions</li>
              <li><strong>Performance Monitoring:</strong> Identify technical issues</li>
              <li><strong>User Journey:</strong> Understand how users navigate the platform</li>
              <li><strong>Preview Feedback:</strong> Collect data to improve the full platform</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">Functional Cookies</h3>
            <p className="text-gray-600 mb-2">
              These enhance your experience on our platform:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li><strong>Language Preferences:</strong> Remember your language choice</li>
              <li><strong>Form Data:</strong> Save partially completed forms</li>
              <li><strong>Display Settings:</strong> Remember your view preferences</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Third-Party Cookies</h2>
            <p className="text-gray-600 mb-2">
              We may use third-party services that place cookies on your device:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li><strong>Google Analytics:</strong> Website traffic and usage analysis</li>
              <li><strong>Cloudflare:</strong> Security and performance optimization</li>
              <li><strong>Other Analytics Tools:</strong> Platform improvement and optimization</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Cookie Categories</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">✅ Strictly Necessary</h4>
                <p className="text-green-700 text-sm">Always active - Required for basic platform functionality</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">📊 Analytics</h4>
                <p className="text-blue-700 text-sm">Help us improve the platform (can be disabled)</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 mb-2">⚙️ Functional</h4>
                <p className="text-purple-700 text-sm">Enhance your experience (can be disabled)</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">🚫 Marketing</h4>
                <p className="text-gray-700 text-sm">Not used in preview version</p>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Managing Your Cookie Preferences</h2>
            
            <h3 className="text-lg font-medium text-gray-800 mb-2">Browser Settings</h3>
            <p className="text-gray-600 mb-2">
              You can control cookies through your browser settings:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
              <li><strong>Firefox:</strong> Preferences → Privacy & Security → Cookies</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
              <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">Platform Settings</h3>
            <p className="text-gray-600 mb-6">
              We will provide cookie preference controls in your account settings once the full platform launches. 
              During preview, you can manage cookies through your browser settings.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Cookie Retention</h2>
            <p className="text-gray-600 mb-2">
              Different cookies have different lifespans:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6">
              <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
              <li><strong>Persistent Cookies:</strong> Remain for a specified period (usually 30 days to 2 years)</li>
              <li><strong>Preview Cookies:</strong> Will be cleared when preview period ends</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Updates to This Policy</h2>
            <p className="text-gray-600 mb-6">
              We may update this cookie policy as we develop our full platform. Any changes will be posted on this page 
              with an updated effective date.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have questions about our use of cookies, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700"><strong>Email:</strong> privacy@stratiqx.ai</p>
              <p className="text-gray-700"><strong>Subject:</strong> Cookie Policy Inquiry</p>
              <p className="text-gray-700"><strong>Website:</strong> preview.stratiqx.ai</p>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                This preview cookie policy will be updated with comprehensive cookie management options upon full platform launch.
              </p>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  )
}